<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ArticleName from '@/components/article/ArticleName.vue'

type BreadcrumbBOP = {
  bop: {
    id: string
    version: number
    productConfiguration: {
      id: string
      name: string
      product: {
        id: string
        name: string
      }
    }
    site: {
      id: string
      name: string
    }
  }
}
type BreadcrumbModule = BreadcrumbBOP & {
  module: {
    id: string
    name: string
  }
}
type BreadcrumbStep = BreadcrumbModule & {
  step: {
    id: string
    name: string
  }
}
type BreadcrumbSerialNumber = BreadcrumbStep & {
  poNumber: string
  serialNumber: string
}
type BreadcrumbArticle = {
  article: {
    articleNumber: string
    revision: string
  }
}

const props = defineProps<{
  breadcrumb: Partial<BreadcrumbSerialNumber & BreadcrumbArticle>
  shortened?: boolean
}>()
const { t } = useI18n()

const bop = computed(() =>
  (props.breadcrumb as BreadcrumbBOP).bop ? (props.breadcrumb as BreadcrumbBOP) : undefined,
)
const module = computed(() =>
  (props.breadcrumb as BreadcrumbModule).module
    ? (props.breadcrumb as BreadcrumbModule)
    : undefined,
)
const step = computed(() =>
  (props.breadcrumb as BreadcrumbStep).step ? (props.breadcrumb as BreadcrumbStep) : undefined,
)
const serialNumber = computed(() =>
  (props.breadcrumb as BreadcrumbSerialNumber).serialNumber !== undefined
    ? (props.breadcrumb as BreadcrumbSerialNumber)
    : undefined,
)
const article = computed(() =>
  (props.breadcrumb as BreadcrumbArticle).article
    ? (props.breadcrumb as BreadcrumbArticle)
    : undefined,
)
</script>

<template>
  <template v-if="bop">
    {{ t('entity.product.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'bopProcess',
        params: {
          productId: bop.bop.productConfiguration.product.id,
          productConfigId: bop.bop.productConfiguration.id,
          siteId: bop.bop.site.id,
          bopId: bop.bop.id,
        },
      }"
    >
      {{ bop.bop.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ bop.bop.productConfiguration.name }}
      <v-icon icon="navigate_next" />
      {{ bop.bop.site.name }}
      <v-icon icon="navigate_next" />
      {{ bop.bop.version }}
    </router-link>
  </template>
  <template v-if="module && !props.shortened">
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: module.bop.productConfiguration.product.id,
          productConfigId: module.bop.productConfiguration.id,
          siteId: module.bop.site.id,
          bopId: module.bop.id,
          moduleId: module.module.id,
        },
      }"
    >
      {{ module.module.name }}
    </router-link>
  </template>
  <template v-if="step && !props.shortened">
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: step.bop.productConfiguration.product.id,
          productConfigId: step.bop.productConfiguration.id,
          siteId: step.bop.site.id,
          bopId: step.bop.id,
          moduleId: step.module.id,
          stepId: step.step.id,
        },
      }"
    >
      {{ step.step.name }}
    </router-link>
  </template>
  <template v-if="serialNumber && !props.shortened">
    <v-icon icon="navigate_next" />
    {{ serialNumber.poNumber }}
    <template v-if="serialNumber.serialNumber"> - {{ serialNumber.serialNumber }}</template>
  </template>
  <template v-if="article">
    {{ t('entity.article.singular') }}
    <v-icon icon="navigate_next" />
    <article-name :article="article.article" />
  </template>
</template>

<style scoped lang="scss"></style>
